import { useState, useEffect } from 'react';
import { 
  CalendarIcon, 
  DownloadIcon, 
  RefreshCw, 
  Filter, 
  ChevronDown,
  Search,
  DollarSign
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { format } from 'date-fns';

const API_URL = "https://pukfh1sc9a.execute-api.ap-south-1.amazonaws.com/prod/api"

export default function SalesDashboard() {
  // State variables
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [dateRange, setDateRange] = useState({
    from: new Date(),
    to: new Date()
  });
  const [filters, setFilters] = useState({
    counsellor: 'all',
    leadSource: 'all',
    course: 'all'
  });
  const [pagination, setPagination] = useState({
    hasMore: false,
    nextToken: null
  });

  // Initial data fetch
  useEffect(() => {
    fetchDeals();
  }, []);

  // Fetch deals with current filters
  const fetchDeals = async (resetPagination = true) => {
    try {
      setLoading(true);
      const fromDate = format(dateRange.from, 'yyyy-MM-dd');
      const toDate = format(dateRange.to, 'yyyy-MM-dd');
      
      let url = `${API_URL}/deals-won?startDate=${fromDate}&endDate=${toDate}`;
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== 'all') {
          url += `&${key}=${value}`;
        }
      });
      
      if (!resetPagination && pagination.nextToken) {
        url += `&lastEvaluatedKey=${pagination.nextToken}`;
      }
      
      const response = await fetch(url);
      const data = await response.json();
      
      setDeals(resetPagination ? data.deals : [...deals, ...data.deals]);
      setPagination(data.pagination);
      
      // Fetch reports with same filters
      fetchReports();
    } catch (error) {
      console.error('Error fetching deals:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch reports
  const fetchReports = async () => {
    try {
      const fromDate = format(dateRange.from, 'yyyy-MM-dd');
      const toDate = format(dateRange.to, 'yyyy-MM-dd');
      
      const response = await fetch(
        `${API_URL}/reports?startDate=${fromDate}&endDate=${toDate}`
      );
      const data = await response.json();
      setReport(data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    }
  };

  // Handle CSV export
  const handleExport = () => {
    const fromDate = format(dateRange.from, 'yyyy-MM-dd');
    const toDate = format(dateRange.to, 'yyyy-MM-dd');
    
    let url = `${API_URL}/export-csv?startDate=${fromDate}&endDate=${toDate}`;
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== 'all') {
        url += `&${key}=${value}`;
      }
    });
    
    window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      {/* Main Dashboard Card */}
      <Card className="w-full mb-8">
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-2xl font-bold">Sales Dashboard</CardTitle>
              <CardDescription>
                Track and analyze sales performance
              </CardDescription>
            </div>
            <Button onClick={handleExport} className="flex items-center gap-2">
              <DownloadIcon className="h-4 w-4" />
              Export CSV
            </Button>
          </div>
        </CardHeader>
        
        <CardContent>
          {/* Filters Section */}
          <div className="flex flex-wrap gap-4 mb-6">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="flex gap-2 min-w-48">
                  <CalendarIcon className="h-4 w-4" />
                  {format(dateRange.from, 'MMM d, yyyy')} - {format(dateRange.to, 'MMM d, yyyy')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  selected={{
                    from: dateRange.from,
                    to: dateRange.to,
                  }}
                  onSelect={(range) => {
                    if (range?.from && range?.to) {
                      setDateRange({ from: range.from, to: range.to });
                    }
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            {/* Filter Dropdowns */}
            {['counsellor', 'leadSource', 'course'].map((filterType) => (
              <Select 
                key={filterType}
                value={filters[filterType]}
                onValueChange={(value) => setFilters(prev => ({...prev, [filterType]: value}))}
              >
                <SelectTrigger className="min-w-40">
                  <SelectValue placeholder={`All ${filterType}s`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All {filterType}s</SelectItem>
                </SelectContent>
              </Select>
            ))}

            <Button onClick={() => fetchDeals()} className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Apply Filters
            </Button>
            
            <Button variant="outline" onClick={() => fetchDeals()} className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
          </div>

          {/* Deals Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Phone Number</TableHead>
                  <TableHead>Lead Source</TableHead>
                  <TableHead>Course</TableHead>
                  <TableHead>Counsellor</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && deals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center">
                      Loading data...
                    </TableCell>
                  </TableRow>
                ) : deals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center">
                      No deals found for the selected filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  deals.map((deal, index) => (
                    <TableRow key={index}>
                      <TableCell>{deal.date}</TableCell>
                      <TableCell>{deal.phoneNumber}</TableCell>
                      <TableCell>{deal.leadSource}</TableCell>
                      <TableCell>{deal.course}</TableCell>
                      <TableCell>{deal.counsellor}</TableCell>
                      <TableCell className="text-right">
                        ₹{deal.amountReceived.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pagination.hasMore && (
            <div className="mt-4 flex justify-center">
              <Button 
                variant="outline" 
                onClick={() => fetchDeals(false)} 
                disabled={loading}
                className="flex items-center gap-2"
              >
                {loading ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
                Load More
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reports Section */}
      {report && (
        <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
          {/* Summary Stats */}
          <Card>
            <CardHeader>
              <CardTitle>Summary Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                  <span className="font-medium">Total Deals</span>
                  <Badge variant="secondary">{report.totalDeals}</Badge>
                </div>
                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                  <span className="font-medium">Total Revenue</span>
                  <Badge variant="secondary">₹{report.totalRevenue.toLocaleString()}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Counsellor Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Counsellor Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(report.counsellorBreakdown).map(([counsellor, stats]) => (
                  <div key={counsellor} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{counsellor}</p>
                      <p className="text-sm text-gray-500">{stats.deals} deals</p>
                    </div>
                    <Badge variant="secondary">₹{stats.revenue.toLocaleString()}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Lead Source Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Lead Source Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(report.leadSourceBreakdown).map(([source, stats]) => (
                  <div key={source} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{source}</p>
                      <p className="text-sm text-gray-500">{stats.count} leads</p>
                    </div>
                    <Badge variant="secondary">₹{stats.revenue.toLocaleString()}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Course Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Course Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(report.courseBreakdown).map(([course, stats]) => (
                  <div key={course} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{course}</p>
                      <p className="text-sm text-gray-500">{stats.count} enrollments</p>
                    </div>
                    <Badge variant="secondary">₹{stats.revenue.toLocaleString()}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}